import type { Numberish, TBaseCommonCode, TBaseResponse, TSelectorItem } from "@/models/common.model"
import type { TSettings, TSettingsRaw } from "@/models/discipline.model"
import type { TSubUnit as SubUnit, Unit, UnitBody, UnitStatus } from "@/models/units.model"
import type { HttpClient } from "@/services/http-common"
import { parse } from "date-fns"
import { tryCatch } from "@/lib/try-catch"
import { UnitsMapper } from "@/mappers/competition/units.mapper"
import { SelectorMapper } from "@/mappers/selectors.mapper"
import ApiServiceFactory from "@/services/api-service-factory"
import { ApiError } from "@/services/http-common"

export type TMassiveUpdateDates = {
  rscCode: string[]
  dateInfo: {
    startDate: string | undefined
    endDate: string | undefined
  }
}

export type TMassiveUpdateVenues = {
  rscCode: string[]
  venueCode: string
}

export type TMassiveUpdateStatus = {
  rscCode: string[]
  status: UnitStatus
}

/**
 * Service for Units API
 */
export class UnitsService {
  private httpClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  /**
   * Fetch all units from the API
   */
  async fetchAllUnits(): Promise<Unit[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<Unit[]>>("/unit/get-all")
      return UnitsMapper.toDto(response.value)
    }
    catch (error) {
      console.error("Error fetching units:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No units found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  /**
   * Fetch units by phase code
   */
  async getUnitsByPhaseCode(phaseCode: string): Promise<Unit[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<Unit[]>>(`/unit/get-by-phase/${phaseCode}`)
      console.log("Units response (raw):", response.value)
      const units = UnitsMapper.toDto(response.value)
      return units
    }
    catch (error) {
      console.error("Error fetching units by phase code:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No units found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async fetchUnitsByCompetitionCode(competitionCode: string): Promise<Unit[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<Unit[]>>(`/unit/get-by-sport-event/${competitionCode}`)
      if (!response || !response.value) {
        console.warn("No units found for competition code:", competitionCode)
        return []
      }
      const { value, isSuccess, message, statusCode } = response
      console.log("Units response (raw):", value)
      if (isSuccess === false) {
        console.error("Error fetching units by competition code:", message, "Status code:", statusCode)
        throw new ApiError(message || "Error fetching units by competition code", statusCode || 500)
      }
      const units = UnitsMapper.toDto(value)
      return units
    }
    catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return []
      }
      throw error
    }
  }

  async getUnitsByUnitCode(unitCode: string): Promise<Unit> {
    try {
      const response = await this.httpClient.get<TBaseResponse<Unit>>(`/unit/get-by-code/${unitCode}`)
      const unit = UnitsMapper.toDto(response.value)

      return unit
    }
    catch (error) {
      console.error("Error fetching units by phase code:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No units found, returning empty array")
        return {} as Unit
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async getSettingsByPhaseCode(phaseCode: string): Promise<TSettings> {
    const response = await tryCatch(this.httpClient.get<TBaseResponse<TSettingsRaw>>(`/phase/competition-details/${phaseCode}`))

    if (response.error) {
      console.error("Error fetching settings by phase code:", response.error)

      // Si el error es 404, devolvemos un objeto vacío
      if (response.error.statusCode === 404) {
        console.info("No settings found for phase code, returning empty object")
        return {} as TSettings
      }

      // Para cualquier otro tipo de error, lo lanzamos
      throw new Error(response.error.message || "Unknown error fetching settings by phase code")
    }

    return {
      ...response.data,
      dateInfo: {
        startDate: response.data.dateInfo?.startDate ? parse(response.data.dateInfo.startDate, "yyyy-MM-dd", new Date()) : undefined,
        endDate: response.data.dateInfo?.endDate ? parse(response.data.dateInfo.endDate, "yyyy-MM-dd", new Date()) : undefined,
      },
    }
  }

  /**
   * Save a new unit
   */
  async saveUnit(data: UnitBody) {
    try {
      return await this.httpClient.post<unknown>("/unit/create-many", data)
    }
    catch (error) {
      console.log("Error saving unit", error)
      throw error
    }
  }

  /**
   * Update an existing unit
   */
  async updateUnit(data: UnitBody & { code: string }) {
    try {
      return await this.httpClient.put<unknown>("/unit/update", data)
    }
    catch (error) {
      console.log("Error updating unit", error)
      throw error
    }
  }

  async deleteUnit(code: string) {
    try {
      return await this.httpClient.delete<unknown>(`/unit/delete/${code}`)
    }
    catch (error) {
      console.log("Error deleting unit", error)
    }
  }

  async fetchUnitTypes(): Promise<TSelectorItem[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<TBaseCommonCode[]>>("/unit-type/get-all")
      const { value } = response
      console.log("Unit types response (raw):", value)
      return SelectorMapper.toDto(value)
    }
    catch (error) {
      console.error("Error fetching unit types:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No unit types found, returning empty array")
        return []
      }
      // Para cualquier otro tipo de error, lo lanzamos
      throw error
    }
  }

  async getUnitByDisciplineCode(disciplineCode: string): Promise<Unit[]> {
    try {
      const response = await this.httpClient.get<TBaseResponse<Unit[]>>(`/unit/get-by-discipline/${disciplineCode}`)
      const units = UnitsMapper.toDto(response.value)

      return units
    }
    catch (error) {
      console.error("Error fetching unit by discipline code:", error)
      throw error
    }
  }

  /**
   * Update unit status
   */
  async updateUnitStatus(unitCode: string, status: UnitStatus): Promise<Unit> {
    try {
      const response = await tryCatch(this.httpClient.put<TBaseResponse<Unit>>(`/unit/update/${unitCode}/status`, { status }))
      console.log("Respuesta del servidor:", response)

      if (response.error) {
        throw new ApiError(response.error.message || "Error actualizando el estado de la unidad", response.error.statusCode || 500)
      }

      return UnitsMapper.toDto(response.data)
    }
    catch (error) {
      console.error("Error actualizando el estado de la unidad:", error)
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError("Error inesperado actualizando el estado de la unidad", 500)
    }
  }

  async updateVenue(unitCode: string, venue: Numberish): Promise<Unit> {
    try {
      const response = await tryCatch(this.httpClient.put<TBaseResponse<Unit>>(`/unit/update-venue/${unitCode}`, { venueCode: venue }))

      if (response.error) {
        console.error("Error updating unit venue:", response.error)
        throw new ApiError(response.error.message || "Error updating unit venue", response.error.statusCode || 500)
      }
      return UnitsMapper.toDto(response.data)
    }
    catch (error) {
      console.error("Error updating unit venues:", error)
      throw error
    }
  }

  async updateDates(unitCode: string, dates: { startDate: string | undefined, endDate: string | undefined }): Promise<void> {
    try {
      const payload = {
        startDate: typeof dates.startDate === "string" ? dates.startDate : dates.startDate,
        endDate: typeof dates.endDate === "string" ? dates.endDate : dates.endDate,
        estimatedStartDate: typeof dates.startDate === "string" ? dates.startDate : dates.startDate,
        estimatedEndDate: typeof dates.endDate === "string" ? dates.endDate : dates.endDate,
      }
      await this.httpClient.put<void>(`/unit/update-dates/${unitCode}`, payload)
    }
    catch (error) {
      console.error("Error updating unit dates:", error)
      throw error
    }
  }

  async updateMasiveDates(settings: TMassiveUpdateDates): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`/unit/massive-update-dates`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las fechas masivamente:", (error as Error).message)
      throw error
    }
  }

  async updateMasiveVenues(settings: TMassiveUpdateVenues): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`/unit/massive-update-venues`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar las venues masivamente:", (error as Error).message)
      throw error
    }
  }

  async updateMasiveStatus(settings: TMassiveUpdateStatus): Promise<void> {
    try {
      const response = await tryCatch(this.httpClient.put<void>(`/unit/massive-update-status`, settings))

      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
    }
    catch (error) {
      console.error("Error al actualizar el status masivamente:", (error as Error).message)
      throw error
    }
  }

  async resetUnitResults(unitCode: string): Promise<Unit> {
    try {
      const response = await tryCatch(this.httpClient.put<TBaseResponse<Unit>>(`/unit/reset-results/${unitCode}`))
      if (response.error) {
        throw new Error(response.error.message || "Unknown error")
      }
      return UnitsMapper.toDto(response.data)
    }
    catch (error) {
      console.error("Error al resetear los resultados de la unidad:", (error as Error).message)
      throw error
    }
  }

  /**
   * Get sub-units for a parent unit
   */
  async getSubUnits(fatherUnitCode: string): Promise<SubUnit[]> {
    try {
      const response = await tryCatch(this.httpClient.get<TBaseResponse<SubUnit[]>>(`/unit/get-sub-units/${fatherUnitCode}`))

      if (response.error) {
        throw new Error(response.error.message || "Error fetching sub-units")
      }

      return response.data || []
    }
    catch (error) {
      console.error("Error fetching sub-units:", error)
      // Si el error es 404, devolvemos un array vacío
      if (error instanceof ApiError && error.status === 404) {
        console.info("No sub-units found, returning empty array")
        return []
      }
      throw error
    }
  }

  /**
   * Create multiple sub-units for a parent unit
   */
  async createSubUnits(data: {
    fatherUnitCode: string
    subUnits: Array<{
      unitNumber: number
      unitTypeCode: string
    }>
  }): Promise<Unit[]> {
    try {
      const response = await tryCatch(this.httpClient.post<TBaseResponse<Unit[]>>("/unit/create-many-sub-units", data))

      if (response.error) {
        throw new Error(response.error.message || "Error creating sub-units")
      }

      return UnitsMapper.toDto(response.data)
    }
    catch (error) {
      console.error("Error creating sub-units:", error)
      throw error
    }
  }
}

// Create and export a singleton instance of the UnitsService
export const unitsService = ApiServiceFactory.createService(UnitsService, "bdm")

export default UnitsService
